<template>
    <tr>
        <td class="dns-zones__table-cell">
            {{ record.type }}
        </td>
        <td class="dns-zones__table-cell | u-text-breakall">
            <span v-if="editableField != 'name'" @click="editField('name')">{{ record.name }}</span>
            <simple-text-input
                v-if="editableField == 'name'"
                v-model="form.new.name"
                :placeholder="trans('page.dns.table.name')"
                :error="form.errors.first('new.name')"
                name="name"
                class="dns-zones__input-small"
                @blur="updateRecord"
                focus
            />
        </td>
        <td class="dns-zones__table-cell | u-text-breakall">
            <span v-if="editableField != 'content'" @click="editField('content')">
                {{ record.content }}
                <strong v-if="contentDetails"> ({{ contentDetails }}) </strong>
            </span>
            <simple-text-input
                v-if="editableField == 'content'"
                v-model="form.new.content"
                :placeholder="trans('page.dns.table.content')"
                :error="form.errors.first('new.content')"
                name="name"
                class="dns-zones__input-small"
                @blur="updateRecord"
                focus
            />

            <mx-modal
                v-if="mxModalVisible"
                @close="closeModal"
                @value="updateMxRecord"
                :type="form.new.type"
                :content="form.new.content"
                :priority="form.new.priority"
            />
            <srv-modal
                v-if="srvModalVisible"
                @close="closeModal"
                @value="updateSrvRecord"
                :type="form.new.type"
                :name="form.new.name"
                :content="form.new.content"
                :priority="form.new.priority"
                :weight="form.new.weight"
                :port="form.new.port"
            />
        </td>
        <td class="dns-zones__table-cell">
            <span v-if="editableField != 'ttl'" @click="editField('ttl')">{{ ttl }}</span>
            <simple-select-input
                v-if="editableField == 'ttl'"
                v-model="form.new.ttl"
                :placeholder="trans('page.dns.table.ttl')"
                :error="form.errors.first('new.ttl')"
                :options="ttls"
                name="ttl"
                class="dns-zones__select-small"
                @blur="updateRecord"
                focus
            />
        </td>
        <td class="dns-zones__table-cell | u-text-right">
            <div class="dns-zones__delete">
                <button class="dns-zones__delete-button" @click="showDeleteModal">
                    <img src="../assets/feather-icons/trash.svg" />
                </button>
            </div>
        </td>

        <confirmation-modal v-if="confirmationModalVisible" @close="closeModal" :record="record" :domain="domain" />
    </tr>
</template>

<script>
import { mapActions } from 'vuex';
import { EventBus } from 'relapp-event';
import ttls from '../config/ttls.json';
import { formMixin, Way2Form } from 'relapp-way2forms';
import { apiMixin } from 'relapp-api';

import MxModal from './modals/MxModal.vue';
import SrvModal from './modals/SrvModal.vue';
import ConfirmationModal from './ConfirmationModal.vue';
import DnsApi from '../services/api/dns.js';

export default {
    components: {
        MxModal,
        SrvModal,
        ConfirmationModal
    },
    mixins: [formMixin, apiMixin],
    props: {
        domain: {
            type: String,
            required: true
        },
        record: Object,
        recordIndex: {
            type: Number,
            required: true
        },
        updating: {
            type: Boolean,
            default: false
        }
    },
    /**
     * Add all the properties found in the data object to Vue’s reactivity system.
     *
     * @return {object}
     */
    data() {
        return {
            form: new Way2Form({
                old: { ...this.record },
                new: { ...this.record }
            }),
            confirmationModalVisible: false,
            field: null,
            editableField: null,
            mxModalVisible: false,
            srvModalVisible: false,
            ttls: ttls
        };
    },
    computed: {
        /**
         * Set the field to edit.
         *
         * @return {string}
         */
        ttl() {
            const ttl = this.ttls.find(ttl => ttl.key == this.record.ttl);

            if (ttl) {
                return ttl.value;
            }

            return this.record.ttl;
        },
        /**
         * Show the content details priority, weight and port.
         *
         * @return {string}
         */
        contentDetails() {
            let contentParts = [];

            if (this.record.priority) {
                contentParts.push(this.record.priority);
            }
            if (this.record.weight) {
                contentParts.push(this.record.weight);
            }
            if (this.record.port) {
                contentParts.push(this.record.port);
            }

            if (contentParts.length) {
                return contentParts.join(' ');
            }
        }
    },
    methods: {
        ...mapActions({
            setDnsStatus: 'dns/setStatus'
        }),
        /**
         * Set the field to edit.
         *
         * @param {string} field
         */
        editField(field) {
            if (this.updating) {
                return;
            }

            this.field = field;
            if (field == 'content') {
                if (this.record.type == 'MX') {
                    this.showMxModal();

                    return;
                } else if (this.record.type == 'SRV') {
                    this.showSrvModal();

                    return;
                }
            }

            this.editableField = field;
        },

        /**
         * Update the DNS record.
         */
        updateRecord() {
            const field = this.field;

            let self = this;
            const oldRecord = { ...this.form.old };
            const newRecord = { ...this.form.new };

            this.editField(null);

            if (JSON.stringify(oldRecord) === JSON.stringify(newRecord)) {
                return;
            }

            this.setDnsStatus(true);

            DnsApi.update(this.form.data(), this.routeParams, {
                oldRecord,
                newRecord,
                recordIndex: self.recordIndex
            })
                .then(() => {
                    self.form.old = { ...newRecord };

                    EventBus.$emit('notification', {
                        type: 'success',
                        message: trans('action.updated')
                    });
                })
                .catch(error => {
                    this.form.errors.record(error.data.errors);
                    self.editField(field);

                    if (error.data) {
                        EventBus.$emit('notification', {
                            type: 'error',
                            message: error.data.message
                        });
                        self.scrollToFirstError();
                    } else {
                        self.handleError(error);
                    }
                })
                .then(() => {
                    self.setDnsStatus(false);
                });
        },

        /**
         * Show the modal if the record type is a MX record.
         */
        showMxModal() {
            this.mxModalVisible = true;
            this.srvModalVisible = false;
        },

        /**
         * Show the modal if the record type is a SRV record.
         */
        showSrvModal() {
            this.mxModalVisible = false;
            this.srvModalVisible = true;
        },

        /**
         * Show the modal if you delete a record
         */
        showDeleteModal() {
            this.confirmationModalVisible = true;
        },

        /**
         * Close the modal.
         */
        closeModal() {
            this.mxModalVisible = false;
            this.srvModalVisible = false;
            this.confirmationModalVisible = false;
        },

        /**
         * Updated value for the mx record.
         *
         * @param {object} record
         */
        updateMxRecord(record) {
            const field = this.field;

            this.form.new.content = record.content;
            this.form.new.priority = parseInt(record.priority);

            const oldRecord = { ...this.form.old };
            const newRecord = { ...this.form.new };

            if (oldRecord[field] === newRecord[field] && oldRecord.priority !== newRecord.priority) {
                this.field = 'priority';
            }

            this.updateRecord();
        },

        /**
         * Updated value for the srv record.
         *
         * @param {object} record
         */
        updateSrvRecord(record) {
            const field = this.field;

            this.form.new.name = record.name;
            this.form.new.priority = parseInt(record.priority);
            this.form.new.weight = parseInt(record.weight);
            this.form.new.port = parseInt(record.port);
            this.form.new.content = record.content;

            const oldRecord = { ...this.form.old };
            const newRecord = { ...this.form.new };

            if (oldRecord[field] === newRecord[field] && oldRecord.priority !== newRecord.priority) {
                this.field = 'priority';
            }

            this.updateRecord();
        }
    },

    watch: {
        record: {
            /**
             * Process the changed record from the store.
             *
             * @param {object} record
             */
            handler(record) {
                this.form.old = { ...record };
                this.form.new = { ...record };
            },
            deep: true
        }
    }
};
</script>
